import React, { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import {
  Typography, Select, Table, Form, Segmented, Tag, Button, message,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import api from '../api';
import { UserContext } from '../App';
import ResizableTitle from '../components/ResizableTitle';

import { selectFilter } from '../utils/selectFilter';
const { Text } = Typography;

const TIER = { silver: '#6b7280', gold: '#16a34a', platinum: '#6366f1', below: '#ef4444' };

// decimals: grade the value as it is shown (e.g. a QA score of 94.96 shown as
// 95.0% grades as 95.0), so the level always matches the number on screen.
function getGoalLevel(value, goal, decimals = null) {
  if (value == null || !goal) return null;
  if (decimals != null) value = Number(Number(value).toFixed(decimals));
  const { direction, SilverValue, TargetValue, PlatinumValue } = goal;
  const lower = direction === 'lower';
  const beats = (t) => t != null && (lower ? value <= t : value >= t);
  const hasTiers = SilverValue != null || PlatinumValue != null;
  if (!hasTiers) {
    if (TargetValue == null) return null;
    return beats(TargetValue) ? 'Meet Goal' : 'Below Goal';
  }
  if (beats(PlatinumValue)) return 'Platinum';
  if (beats(TargetValue))   return 'Gold';
  if (beats(SilverValue))   return 'Silver';
  return 'Below Goal';
}

// The level achieved across the three Superior Stars metrics, which is the
// weakest of them: all Platinum is Platinum, Gold and Platinum together is
// Gold, anything reaching down to Silver is Silver, and a single Below Goal
// makes the set Below Goal.
//
// Undecidable rather than worse when a metric has no level at all -- no goal
// configured, or no value to grade. Treating a missing metric as a failure
// would quietly mark everyone Below Goal for the whole of a quarter whose
// adherence points are not loaded yet.
const STAR_RANK    = { Platinum: 3, Gold: 2, 'Meet Goal': 2, Silver: 1, 'Below Goal': 0 };
const STAR_BY_RANK = { 3: 'Platinum', 2: 'Gold', 1: 'Silver', 0: 'Below Goal' };

function combinedLevel(levels) {
  if (!levels.length || levels.some((l) => !l)) return null;
  return STAR_BY_RANK[Math.min(...levels.map((l) => STAR_RANK[l] ?? 0))];
}

// The Stars Adherence Level is uploaded as the level itself (Admin > File
// Uploads): 0 Platinum, 1 Gold, 2 Silver, 3 did not achieve. No goal grades it.
const ADH_LEVEL = { 0: 'Platinum', 1: 'Gold', 2: 'Silver', 3: 'Below Goal' };
const adhLevel = (v) => (v == null ? null : ADH_LEVEL[v] ?? null);

function LevelTag({ level, noGoal }) {
  if (noGoal) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
  if (!level)  return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
  const bg =
    level === 'Platinum'   ? TIER.platinum :
    level === 'Gold'       ? TIER.gold     :
    level === 'Silver'     ? TIER.silver   :
    level === 'Meet Goal'  ? TIER.gold     :
    TIER.below;
  return (
    <div style={{ textAlign: 'center' }}>
      <Tag style={{ margin: 0, fontSize: 11, fontWeight: 600, background: bg, color: '#fff', border: 'none' }}>
        {level}
      </Tag>
    </div>
  );
}

function fmtTime(seconds) {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function pctColor(pct) {
  if (pct == null) return '#d9d9d9';
  if (pct >= 70)   return '#52c41a';
  if (pct >= 50)   return '#1677ff';
  if (pct >= 30)   return '#fa8c16';
  return '#ff4d4f';
}

function ordinal(n) {
  if (n == null) return null;
  const m100 = n % 100, m10 = n % 10;
  if (m100 >= 11 && m100 <= 13) return `${n}th`;
  if (m10 === 1) return `${n}st`;
  if (m10 === 2) return `${n}nd`;
  if (m10 === 3) return `${n}rd`;
  return `${n}th`;
}

function PctCell({ value, percentile, rank, total, fmt }) {
  if (percentile == null) return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>;
  const color = pctColor(percentile);
  const display = fmt ? fmt(value) : (value != null ? String(value) : '—');
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 12, fontWeight: 600 }}>{display}</div>
      {rank != null && total != null && (
        <div style={{ fontSize: 10, color, fontWeight: 500, marginTop: 1 }}>{rank}/{total}</div>
      )}
    </div>
  );
}

/**
 * `embedded` renders the Goals view alone, for one campaign, without the
 * heading, the Stats/Goals switch or the campaign, team and job filters. The
 * month picker stays: these figures are a month's standing, not a range's, and
 * the month is the only thing left to choose.
 *
 * Used by the Campaign Summary's Agent Goals tab.
 */
// What Error % divides by: InboundCalls unless a historical call count was
// backfilled for the month (ErrorCallQty, refresh step 21b).
const errCalls = (r) => r.ErrorCallQty ?? r.InboundCalls;

export default function LeaderboardPage({ embedded = false, campaignId = null, teamId: teamIdProp = null }) {
  const currentUser = useContext(UserContext);
  const navigate    = useNavigate();

  // Agent mode. Agents see the Stats tab only, pick from the months and
  // campaigns they personally have stats rows for, get no Team filter (they
  // are compared across their whole campaign, not their team) and default to
  // the Agent job. The API enforces all of this again server-side -- this is
  // the presentation half, not the security boundary.
  const isAgent = currentUser?.Role === 'agent';

  // Agents and support agents see contact volume only. The QA, compliance,
  // timecard, upsell and cheers groups stay internal. The three identity
  // columns are kept regardless -- a leaderboard with no agent name, month or
  // campaign would be unreadable.
  const isLimitedView = isAgent || currentUser?.Role === 'support_agent';
  const canSeeStars = ['admin', 'qa_admin'].includes(currentUser?.Role) || currentUser?.JobID === 6;
  // Adherence (Goals view and its summary, Stats view): Oversight Stats View and up.
  const canSeeAdherence = (currentUser?.CanViewOversightStats ?? 0) >= 1;

  const [colWidths, setColWidths] = useState({});
  const toResizable = (col) => ({
    ...col,
    width: colWidths[col.key] ?? col.width ?? 120,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: (_, { size }) => setColWidths((p) => ({ ...p, [col.key]: size.width })),
    }),
  });

  const [selectedMonth, setSelectedMonth]     = useState(null);
  const [monthOptions, setMonthOptions]       = useState([]);
  const [campaign, setCampaign]               = useState(null);
  const [teamId, setTeamId]                   = useState(null);
  const [jobId, setJobId]                     = useState(null);
  const [campaignOptions, setCampaignOptions] = useState([]);
  const [teamOptions, setTeamOptions]         = useState([]);
  const [jobOptions, setJobOptions]           = useState([]);

  const [lbData, setLbData]       = useState([]);
  const [lbLoading, setLbLoading] = useState(false);
  const [view, setView]           = useState(embedded ? 'goals' : 'stats');
  const [goals, setGoals]         = useState([]);

  // Superior Stars: a quarter graded as one period rather than as three
  // months that each get their own verdict.
  const [starsQuarter, setStarsQuarter] = useState(null);
  const [starsData,    setStarsData]    = useState([]);
  const [starsMonths,  setStarsMonths]  = useState([]);
  const [starsLoading, setStarsLoading] = useState(false);

  useEffect(() => {
    api.get('/api/leaderboard/months')
      .then((r) => {
        const opts = r.data.map((m) => ({ label: m, value: m }));
        setMonthOptions(opts);
        if (opts.length) setSelectedMonth(opts[0].value);
      })
      .catch(() => {});
    if (isAgent) {
      api.get('/api/leaderboard/my-campaigns')
        .then((r) => setCampaignOptions((r.data || []).map((c) => ({ label: c.Name, value: c.ID }))))
        .catch(() => {});
    } else {
      api.get('/api/agent-stats/options')
        .then((r) => setCampaignOptions(r.data.campaigns.map((c) => ({ label: c.Name, value: c.ID }))))
        .catch(() => {});
      api.get('/api/teams', { params: { no_client_users: 1 } })
        .then((r) => {
          const all = r.data || [];
          const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
          const visible = vtIds.length > 0 ? all.filter((t) => vtIds.includes(t.ID)) : all;
          setTeamOptions(
            visible
              .filter((t) => t.IncludeInRankings !== 0)
              .map((t) => ({ label: t.Name, value: t.ID }))
          );
        })
        .catch(() => {});
    }
    api.get('/api/jobs')
      .then((r) => setJobOptions(
        (r.data || [])
          .filter((j) => j.IncludeInStats)
          .map((j) => ({ label: j.JobName, value: j.JobID }))
      ))
      .catch(() => {});
  }, [currentUser, isAgent]);

  // Goals drive the Goals tab only, which the limited view never shows. Not
  // fetching them keeps per-agent goal attainment out of the browser as well
  // as off the screen.
  // Embedded: follow the campaign and team chosen on the page above.
  //
  // The team matters here and is not merely decoration: an agent belongs to
  // one team but may take another campaign's calls, so campaign and team cut
  // across each other. Without it this showed every agent on the campaign
  // whatever team was selected above.
  useEffect(() => {
    if (!embedded) return;
    setCampaign(campaignId ? String(campaignId) : undefined);
    setTeamId(teamIdProp ?? null);
  }, [embedded, campaignId, teamIdProp]);

  useEffect(() => {
    if (isLimitedView) return;
    api.get('/api/goals').then((r) => setGoals(r.data || [])).catch(() => {});
  }, [isLimitedView]);

  // Agents start on the Agent job rather than "All jobs". Runs once the job
  // list arrives and only while nothing has been chosen, so it never fights a
  // deliberate change.
  useEffect(() => {
    if (!isAgent || jobId != null || !jobOptions.length) return;
    const agentJob = jobOptions.find((j) => j.label === 'Agent');
    if (agentJob) setJobId(agentJob.value);
  }, [isAgent, jobOptions, jobId]);


  const fetchLeaderboard = useCallback(() => {
    if (!campaign) { setLbData([]); return; }
    setLbLoading(true);
    const params = { campaign };
    if (selectedMonth) { params.month_from = selectedMonth; params.month_to = selectedMonth; }
    if (teamId && !isAgent) params.team_id = teamId;
    if (jobId)              params.job_id  = jobId;
    api.get('/api/leaderboard', { params })
      .then((r) => {
        setLbData(r.data.rows ?? []);
        if (r.data.months?.length) setMonthOptions(r.data.months.map((m) => ({ label: m, value: m })));
        if (!selectedMonth && r.data.month_from) setSelectedMonth(r.data.month_from);
      })
      .catch(() => setLbData([]))
      .finally(() => setLbLoading(false));
  }, [selectedMonth, campaign, teamId, jobId, isAgent]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Derived from the months that actually have data rather than generated, so
  // the list cannot offer a quarter with nothing in it.
  const quarterOptions = useMemo(() => {
    const qs = new Set();
    monthOptions.forEach(({ value }) => {
      const [y, m] = String(value).split('-');
      if (y && m) qs.add(`${y}-Q${Math.floor((Number(m) - 1) / 3) + 1}`);
    });
    return [...qs].sort().reverse().map((q) => ({ label: q, value: q }));
  }, [monthOptions]);

  useEffect(() => {
    if (view !== 'stars' || starsQuarter || !quarterOptions.length) return;
    setStarsQuarter(quarterOptions[0].value);
  }, [view, starsQuarter, quarterOptions]);

  useEffect(() => {
    if (view !== 'stars' || !campaign || !starsQuarter) { setStarsData([]); return; }
    setStarsLoading(true);
    api.get('/api/leaderboard/superior-stars', {
      params: {
        quarter: starsQuarter,
        campaign,
        ...(teamId ? { team_id: teamId } : {}),
        ...(jobId  ? { job_id:  jobId  } : {}),
      },
    })
      .then((r) => { setStarsData(r.data.rows || []); setStarsMonths(r.data.months || []); })
      .catch(() => { setStarsData([]); setStarsMonths([]); })
      .finally(() => setStarsLoading(false));
  }, [view, campaign, starsQuarter, teamId, jobId]);

  const quarterEndMonth = useMemo(() => {
    if (!starsQuarter) return null;
    const [y, q] = String(starsQuarter).split('-Q');
    return `${y}-${String(Number(q) * 3).padStart(2, '0')}`;
  }, [starsQuarter]);

  // The goal in force at quarter end, one per metric. Campaign-specific
  // first, then the Overall row -- the same fallback resolve_goals applies
  // server-side, which matters here because the figures pool every campaign
  // the agent worked rather than belonging to the one that was selected.
  //
  // Held outside the column builder so the export grades rows through the
  // same lookup instead of reimplementing the bands.
  const starGoals = useMemo(() => {
    if (!quarterEndMonth) return {};
    const campId = campaign ? Number(campaign) : null;
    const inForce = (cid, goalType) => goals.find((g) =>
      g.GoalType === goalType && g.CampaignID === cid &&
      g.StartMonth <= quarterEndMonth &&
      (!g.EndMonth || g.EndMonth >= quarterEndMonth)) ?? null;
    return {
      AvgQAScore: inForce(campId, 'AvgQAScore') ?? inForce(0, 'AvgQAScore'),
      ErrorPct:   inForce(campId, 'ErrorPct')   ?? inForce(0, 'ErrorPct'),
    };
  }, [goals, campaign, quarterEndMonth]);

  const starLevels = useCallback((r) => [
    getGoalLevel(r.QAScore,        starGoals.AvgQAScore      ?? null, 1),
    getGoalLevel(r.ErrorPct,       starGoals.ErrorPct        ?? null, 2),
    adhLevel(r.AdherenceLevel),
  ], [starGoals]);

  const starColumns = useMemo(() => {
    const monthCount = starsMonths.length || 3;
    const num = (v) => v == null ? null : Number(v).toLocaleString('en-US');

    const metricCol = (title, goalType, getValue, fmtVal, width, decimals) => {
      const goal = starGoals[goalType] ?? null;
      return {
        title, key: goalType, width, align: 'center',
        sorter: (a, b) => (getValue(a) ?? -Infinity) - (getValue(b) ?? -Infinity),
        render: (_, r) => {
          const value = getValue(r);
          return (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 11, color: value != null ? '#595959' : '#bfbfbf', marginBottom: 2 }}>
                {value != null ? fmtVal(value) : '—'}
              </div>
              <LevelTag level={getGoalLevel(value, goal, decimals)} noGoal={!goal} />
            </div>
          );
        },
      };
    };

    return [
      {
        title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 200, fixed: 'left',
        sorter: (a, b) => (a.DisplayName ?? '').localeCompare(b.DisplayName ?? ''),
        render: (name, r) => (
          <div>
            {(currentUser?.CanViewAgentSummary ?? 0) >= 1
              ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r.EmployeeID)}`)}>{name}</Typography.Link>
              : <span>{name}</span>}
            {/* Tenure is context for a quarter's figures: someone hired in the
                second month has a thinner quarter than the row suggests. */}
            {r.PaycorHireDate && (
              <div style={{ fontSize: 10, color: '#8c8c8c' }}>Hired {r.PaycorHireDate}</div>
            )}
          </div>
        ),
      },
      {
        title: 'Campaigns', dataIndex: 'Campaigns', key: 'Campaigns', width: 200, ellipsis: true,
        render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
      },
      {
        title: 'Status', dataIndex: 'PaycorStatus', key: 'PaycorStatus', width: 130,
        sorter: (a, b) => (a.PaycorStatus ?? '').localeCompare(b.PaycorStatus ?? ''),
        render: (v, r) => {
          if (!v && !r.PaycorTermDate) return <Typography.Text type="secondary">—</Typography.Text>;
          const lower = (v || '').toLowerCase();
          const color = lower === 'active' ? '#389e0d'
            : lower === 'terminated' ? '#cf1322' : '#8c8c8c';
          return (
            <div>
              <span style={{ color }}>{v || 'No longer employed'}</span>
              {r.PaycorTermDate && (
                <div style={{ fontSize: 10, color: '#8c8c8c' }}>Left {r.PaycorTermDate}</div>
              )}
            </div>
          );
        },
      },
      {
        title: 'Eligible', key: 'Eligible', width: 115, align: 'center',
        sorter: (a, b) => (a.Eligible === b.Eligible ? 0 : a.Eligible ? -1 : 1),
        render: (_, r) => (
          <div style={{ textAlign: 'center' }}>
            <Tag color={r.Eligible ? 'green' : 'orange'} style={{ margin: 0 }}>
              {r.Eligible ? 'Eligible' : 'Ineligible'}
            </Tag>
            {/* The failing condition, not just the verdict. Both can fail at
                once, and departure is the one that cannot be fixed. */}
            <div style={{ fontSize: 10, color: '#8c8c8c', marginTop: 2 }}>
              {r.LeftBeforeQuarterEnd
                ? 'left mid-quarter'
                : !r.ScoredAllMonths ? `scored in ${r.QAMonths} of ${monthCount}`
                  : !r.HasAdherenceLevel ? 'no adherence level' : `scored in ${r.QAMonths} of ${monthCount}`}
            </div>
          </div>
        ),
      },
      {
        title: 'Stats', key: 'Stats', width: 220,
        sorter: (a, b) => (a.EvalCount ?? -1) - (b.EvalCount ?? -1),
        render: (_, r) => (
          <div style={{ fontSize: 11, color: '#595959', lineHeight: 1.7 }}>
            <div>Evals: <strong>{num(r.EvalCount) ?? '—'}</strong></div>
            <div>
              Inbound Calls: <strong>{num(r.InboundCalls) ?? '—'}</strong>
              {'\u2002'}Errors: <strong>{num(r.ErrorCount) ?? 0}</strong>
            </div>
            {/* Which month the figure was imported against, not how many of
                them carry one: the whole quarter is normally keyed to its
                last month, so a count would report every quarter as short. */}
            <div style={{ color: r.HasAdherenceLevel ? '#8c8c8c' : '#d46b08' }}>
              Stars Adherence Level: {r.HasAdherenceLevel ? 'loaded' : 'not loaded'}
            </div>
          </div>
        ),
      },
      metricCol('QA Score %', 'AvgQAScore', (r) => r.QAScore, (v) => `${Number(v).toFixed(1)}%`, 125, 1),
      metricCol('Errors %',   'ErrorPct',   (r) => r.ErrorPct, (v) => `${Number(v).toFixed(2)}%`, 125, 2),
      {
        title: 'Stars Adherence Level', key: 'AdherenceLevel', width: 145, align: 'center',
        sorter: (a, b) => (a.AdherenceLevel ?? Infinity) - (b.AdherenceLevel ?? Infinity),
        render: (_, r) => (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: r.AdherenceLevel != null ? '#595959' : '#bfbfbf', marginBottom: 2 }}>
              {r.AdherenceLevel != null ? r.AdherenceLevel : 'no record'}
            </div>
            <LevelTag level={adhLevel(r.AdherenceLevel)} />
          </div>
        ),
      },
      {
        title: 'Level Achieved', key: 'Achieved', width: 130, align: 'center',
        sorter: (a, b) => (STAR_RANK[combinedLevel(starLevels(a))] ?? -1)
                        - (STAR_RANK[combinedLevel(starLevels(b))] ?? -1),
        render: (_, r) => {
          const level = combinedLevel(starLevels(r));
          if (!level) return <Typography.Text type="secondary" style={{ fontSize: 11 }}>—</Typography.Text>;
          // Shown for everyone, earned only by the eligible. Dimming it keeps
          // the fact visible without reading as an award next to Ineligible.
          return (
            <span style={{ opacity: r.Eligible ? 1 : 0.45 }}>
              <LevelTag level={level} />
            </span>
          );
        },
      },
    ];
  }, [starGoals, starLevels, starsMonths, currentUser, navigate]);

  // One row per agent, values and levels side by side. Levels come from the
  // same lookup the table uses, so the sheet cannot disagree with the screen.
  const exportStars = () => {
    if (!starsData.length) { message.warning('No data to export.'); return; }
    const monthCount = starsMonths.length || 3;  // eligibility denominator
    const lvl = (goalType, value) =>
      getGoalLevel(value, starGoals[goalType] ?? null, { AvgQAScore: 1, ErrorPct: 2 }[goalType]) ?? '';
    const rows = starsData.map((r) => ({
      'Agent':              r.DisplayName,
      'Hire Date':          r.PaycorHireDate || '',
      'Job':                r.JobName || '',
      'Campaigns':          r.Campaigns || '',
      'Status':             r.PaycorStatus || '',
      'Term Date':          r.PaycorTermDate || '',
      'Eligible':           r.Eligible ? 'Eligible' : 'Ineligible',
      'Ineligible Reason':  r.Eligible ? ''
        : [!r.ScoredAllMonths && 'not scored in every month',
           r.LeftBeforeQuarterEnd && 'not employed at quarter end',
           !r.HasAdherenceLevel && 'no Stars Adherence Level'].filter(Boolean).join('; '),
      'Months Scored':      r.QAMonths,
      'Months In Quarter':  monthCount,
      'Evals':              r.EvalCount ?? null,
      'QA Score %':         r.QAScore ?? null,
      'QA Score Level':     lvl('AvgQAScore', r.QAScore),
      'Inbound Calls':      r.InboundCalls ?? null,
      'Errors':             r.ErrorCount ?? 0,
      'Errors %':           r.ErrorPct ?? null,
      'Errors Level':       lvl('ErrorPct', r.ErrorPct),
      'Stars Adherence Level': r.AdherenceLevel ?? null,
      'Stars Adherence Level Achieved': adhLevel(r.AdherenceLevel) || '',
      'Level Achieved':     combinedLevel(starLevels(r)) || '',
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'Superior Stars');
    XLSX.writeFile(wb, `superior_stars_${starsQuarter}.xlsx`);
  };

  const showTeamCol = !teamId;

  // ── Stats (leaderboard) columns ─────────────────────────────────────────────
  const lbn   = (v) => v != null ? v : '—';
  const lbh   = (v) => v != null && v > 0 ? Number(v).toFixed(1) : '—';
  const lbpct = (v) => v != null ? `${Number(v).toFixed(1)}%` : '—';

  const pctMap = useMemo(() => {
    if (!lbData.length) return {};
    const METRICS = [
      { key: 'AvgTalkSecs',         lob: true  },
      { key: 'AvgACWSecs',          lob: true  },
      { key: 'AvgHTSecs',           lob: true  },
      { key: 'AvgQAScore',          lob: false },
      { key: 'OccurrenceCount',     lob: true  },
      { key: 'ErrorCount',          lob: true  },
      { key: 'ComplianceCount',     lob: true  },
      { key: 'RecoachingCount',     lob: true  },
      { key: 'AdherencePct',        lob: false },
      { key: 'ShortTermNoticeCount',lob: true  },
    ];
    const byMonth = {};
    for (const r of lbData) {
      (byMonth[r.MonthKey] = byMonth[r.MonthKey] || []).push(r);
    }
    const result = {};
    for (const rows of Object.values(byMonth)) {
      for (const m of METRICS) {
        const vals = rows.map(r => r[m.key]).filter(v => v != null);
        if (vals.length < 2) continue;
        const total = vals.length;
        for (const r of rows) {
          const k = `${r.EmployeeID}-${r.MonthKey}`;
          if (!result[k]) result[k] = {};
          const v = r[m.key];
          if (v == null) { result[k][m.key] = null; continue; }
          const below = vals.filter(x => x < v).length;
          const above = vals.filter(x => x > v).length;
          const percentile = m.lob
            ? Math.round((1 - below / total) * 100)
            : Math.round(below / total * 100);
          const rank = m.lob ? below + 1 : above + 1;
          result[k][m.key] = { percentile, rank, total };
        }
      }
    }
    return result;
  }, [lbData]);

  const p = (key) => (v, r) => {
    const cell = pctMap[`${r.EmployeeID}-${r.MonthKey}`]?.[key];
    const fmt = key === 'AvgQAScore' || key === 'AdherencePct' ? lbpct :
                key === 'AvgTalkSecs' || key === 'AvgACWSecs' || key === 'AvgHTSecs' ? fmtTime :
                lbn;
    return <PctCell value={v} percentile={cell?.percentile} rank={cell?.rank} total={cell?.total} fmt={fmt} />;
  };

  const goalColumns = useMemo(() => {
    const campId = campaign ? Number(campaign) : null;

    function findG(r, goalType) {
      const cid = goalType === 'AdherencePct' ? 0 : campId;
      // Monthly view: a quarterly goal grades a quarter's figure, never one
      // month's, so those rows are not candidates here at all.
      const matches = goals.filter((g) =>
        g.period !== 'quarter' &&
        g.GoalType === goalType &&
        g.CampaignID === cid &&
        g.StartMonth <= r.MonthKey &&
        (!g.EndMonth || g.EndMonth >= r.MonthKey)
      );
      if (!matches.length) return null;
      if (goalType === 'UpsellCount' && r.PaycorFTStatus != null) {
        const agentFT = r.PaycorFTStatus === 'FT' ? 1 : 0;
        const specific = matches.find((g) => g.FullTime != null && (g.FullTime ? 1 : 0) === agentFT);
        if (specific) return specific;
        return matches.find((g) => g.FullTime == null) ?? matches[0];
      }
      return matches[0];
    }

    function makeCol(title, goalType, getValue, fmtVal, decimals) {
      return {
        title,
        key: `goal_${goalType}`,
        width: 120,
        align: 'center',
        render: (_, r) => {
          const goal  = findG(r, goalType);
          const value = getValue(r);
          const level = getGoalLevel(value, goal, decimals);
          return (
            <div style={{ textAlign: 'center' }}>
              {value != null && (
                <div style={{ fontSize: 11, color: '#595959', marginBottom: 2 }}>{fmtVal(value)}</div>
              )}
              <LevelTag level={level} noGoal={!goal} />
            </div>
          );
        },
      };
    }

    return [
      { title: 'Month',    dataIndex: 'MonthKey',    key: 'MonthKey',    width: 90,  fixed: 'left', sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') },
      { title: 'Campaign', dataIndex: 'Campaign',    key: 'Campaign',    width: 130, fixed: 'left' },
      {
        title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 200, fixed: 'left',
        sorter: (a, b) => (a.DisplayName ?? '').localeCompare(b.DisplayName ?? ''),
        // Job and Paycor hire date underneath: the rehire date when there is
        // one, as Census and Adherence do (routes/leaderboard.py
        // PaycorHireDate). Paycor's 1900 placeholder is no date.
        render: (name, r) => {
          const hired = r.PaycorHireDate && !r.PaycorHireDate.startsWith('1900') ? r.PaycorHireDate : null;
          return (
          <div>
            {(currentUser?.CanViewAgentSummary ?? 0) >= 1
              ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r.EmployeeID)}`)}>{name}</Typography.Link>
              : <span>{name}</span>}
            {(r.JobName || hired) && (
              <div><Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {[r.JobName, hired && `Hired ${hired.slice(5, 7)}/${hired.slice(8, 10)}/${hired.slice(0, 4)}`]
                  .filter(Boolean).join(' · ')}
              </Typography.Text></div>
            )}
          </div>
          );
        },
      },
      makeCol('QA Score %',    'AvgQAScore',      (r) => r.AvgQAScore,                                             (v) => `${Number(v).toFixed(1)}%`, 1),
      makeCol('Errors %',      'ErrorPct',        (r) => errCalls(r) ? r.ErrorCount / errCalls(r) * 100 : null, (v) => `${v.toFixed(1)}%`, 1),
      makeCol('AHT',           'AvgHTSecs',       (r) => r.AvgHTSecs,                                               (v) => fmtTime(Math.round(v)), 0),
      makeCol('Upsells',       'UpsellCount',     (r) => r.UpsellCount,                                             (v) => `${v}`),
      ...(canSeeAdherence ? [makeCol('Adherence %',   'AdherencePct',    (r) => r.AdherencePct,                                            (v) => `${Number(v).toFixed(1)}%`, 1)] : []),
    ];
  }, [goals, campaign, navigate, canSeeAdherence]);

  const goalSummary = useMemo(() => {
    if (!lbData.length || !goals.length || !campaign) return [];
    const campId = Number(campaign);

    const METRICS = [
      { label: 'QA Score %',    goalType: 'AvgQAScore',      getValue: (r) => r.AvgQAScore, decimals: 1 },
      { label: 'Errors %',      goalType: 'ErrorPct',        getValue: (r) => errCalls(r) ? r.ErrorCount / errCalls(r) * 100 : null, decimals: 1 },
      ...(canSeeAdherence ? [{ label: 'Adherence %',   goalType: 'AdherencePct',    getValue: (r) => r.AdherencePct, decimals: 1 }] : []),
      { label: 'AHT',           goalType: 'AvgHTSecs',       getValue: (r) => r.AvgHTSecs, decimals: 0 },
      { label: 'Upsells',       goalType: 'UpsellCount',     getValue: (r) => r.UpsellCount, perRowGoal: true },
    ];

    return METRICS.map(({ label, goalType, getValue, perRowGoal, decimals }) => {
      const cid = goalType === 'AdherencePct' ? 0 : campId;
      const counts = { Platinum: 0, Gold: 0, Silver: 0, Below: 0 };
      let goalFound = false;

      for (const r of lbData) {
        let goal;
        if (perRowGoal) {
          // Per-row goal lookup — needed for UpsellCount FullTime split
          const matches = goals.filter((g) =>
            g.period !== 'quarter' &&
            g.GoalType === goalType &&
            g.CampaignID === campId &&
            g.StartMonth <= r.MonthKey &&
            (!g.EndMonth || g.EndMonth >= r.MonthKey)
          );
          if (!matches.length) continue;
          if (r.PaycorFTStatus != null) {
            const agentFT = r.PaycorFTStatus === 'FT' ? 1 : 0;
            const specific = matches.find((g) => g.FullTime != null && (g.FullTime ? 1 : 0) === agentFT);
            goal = specific ?? matches.find((g) => g.FullTime == null) ?? matches[0];
          } else {
            goal = matches.find((g) => g.FullTime == null) ?? matches[0];
          }
          // null UpsellCount = agent not in upsell campaign, skip
          if (getValue(r) == null) continue;
        } else {
          goal = goals.find((g) =>
            g.period !== 'quarter' &&
            g.GoalType === goalType &&
            g.CampaignID === cid &&
            g.StartMonth <= r.MonthKey &&
            (!g.EndMonth || g.EndMonth >= r.MonthKey)
          ) ?? null;
          if (!goal) continue;
        }
        goalFound = true;
        const level = getGoalLevel(getValue(r), goal, decimals);
        if      (level === 'Platinum')                counts.Platinum++;
        else if (level === 'Gold' || level === 'Meet Goal') counts.Gold++;
        else if (level === 'Silver')                  counts.Silver++;
        else if (level === 'Below Goal')              counts.Below++;
      }

      if (!goalFound) return null;
      return { key: goalType, label, ...counts };
    }).filter(Boolean);
  }, [lbData, goals, campaign, canSeeAdherence]);

  const summaryColumns = [
    { title: 'Goal', dataIndex: 'label', key: 'label', width: 140 },
    { title: 'Platinum',   dataIndex: 'Platinum', key: 'platinum', width: 90,  align: 'center',
      render: (v) => v > 0 ? <Tag style={{ background: TIER.platinum, color: '#fff', border: 'none', fontWeight: 600, margin: 0 }}>{v}</Tag> : <Text type="secondary">0</Text> },
    { title: 'Gold',       dataIndex: 'Gold',     key: 'gold',     width: 80,  align: 'center',
      render: (v) => v > 0 ? <Tag style={{ background: TIER.gold,     color: '#fff', border: 'none', fontWeight: 600, margin: 0 }}>{v}</Tag> : <Text type="secondary">0</Text> },
    { title: 'Silver',     dataIndex: 'Silver',   key: 'silver',   width: 80,  align: 'center',
      render: (v) => v > 0 ? <Tag style={{ background: TIER.silver,   color: '#fff', border: 'none', fontWeight: 600, margin: 0 }}>{v}</Tag> : <Text type="secondary">0</Text> },
    { title: 'Below Goal', dataIndex: 'Below',    key: 'below',    width: 100, align: 'center',
      render: (v) => v > 0 ? <Tag style={{ background: TIER.below,    color: '#fff', border: 'none', fontWeight: 600, margin: 0 }}>{v}</Tag> : <Text type="secondary">0</Text> },
  ];

  const lbColumns = [
    { title: 'Month',    dataIndex: 'MonthKey',    key: 'MonthKey',    width: 90,  fixed: 'left', sorter: (a, b) => (a.MonthKey ?? '').localeCompare(b.MonthKey ?? '') },
    { title: 'Campaign', dataIndex: 'Campaign',    key: 'Campaign',    width: 130, fixed: 'left', sorter: (a, b) => (a.Campaign ?? '').localeCompare(b.Campaign ?? '') },
    {
      title: 'Agent', dataIndex: 'DisplayName', key: 'DisplayName', width: 200, fixed: 'left',
      sorter: (a, b) => (a.DisplayName ?? '').localeCompare(b.DisplayName ?? ''),
      render: (name, r) => {
        const lower = (r.PaycorStatus || '').toLowerCase();
        const statusColor = lower === 'active' ? '#52c41a' : lower === 'terminated' ? '#ff4d4f' : '#8c8c8c';
        return (
          <div>
            {(currentUser?.CanViewAgentSummary ?? 0) >= 1
              ? <Typography.Link onClick={() => navigate(`/agents?user_id=${encodeURIComponent(r.EmployeeID)}`)}>{name}</Typography.Link>
              : <span>{name}</span>
            }
            {(r.JobName || r.PaycorStatus || r.PaycorHireDate || r.PaycorFTStatus) && (
              <div style={{ fontSize: 10, color: '#8c8c8c', lineHeight: 1.4, marginTop: 1 }}>
                {r.JobName && <span>{r.JobName}</span>}
                {r.JobName && r.PaycorStatus && <span> · </span>}
                {r.PaycorStatus && <span style={{ color: statusColor }}>{r.PaycorStatus}</span>}
                {(r.PaycorStatus || r.JobName) && r.PaycorHireDate && <span> · </span>}
                {r.PaycorHireDate && <span>{r.PaycorHireDate}</span>}
                {r.PaycorHireDate && r.PaycorFTStatus && <span> · </span>}
                {r.PaycorFTStatus && <span>{r.PaycorFTStatus}</span>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Contact Stats (Inbound)',
      children: [
        { title: 'Calls',    dataIndex: 'InboundCalls',  key: 'InboundCalls',  width: 65,  align: 'right',  sorter: (a, b) => (a.InboundCalls  ?? 0) - (b.InboundCalls  ?? 0), render: lbn },
        { title: 'Chats',    dataIndex: 'InboundChats',  key: 'InboundChats',  width: 65,  align: 'right',  sorter: (a, b) => (a.InboundChats  ?? 0) - (b.InboundChats  ?? 0), render: lbn },
        // Busiest agents first on load. Nulls coalesce to 0 so agents with no
        // contacts settle at the bottom rather than jumping to the top.
        { title: 'Total',    dataIndex: 'TotalContacts', key: 'TotalContacts', width: 65,  align: 'right',  sorter: (a, b) => (a.TotalContacts ?? 0) - (b.TotalContacts ?? 0), defaultSortOrder: 'descend', render: lbn },
        { title: 'Avg Talk', dataIndex: 'AvgTalkSecs',  key: 'AvgTalkSecs',  width: 80,  align: 'center', sorter: (a, b) => (a.AvgTalkSecs  ?? 0) - (b.AvgTalkSecs  ?? 0), render: p('AvgTalkSecs') },
        { title: 'Avg ACW',  dataIndex: 'AvgACWSecs',   key: 'AvgACWSecs',   width: 80,  align: 'center', sorter: (a, b) => (a.AvgACWSecs ?? 0) - (b.AvgACWSecs ?? 0),   render: p('AvgACWSecs') },
        { title: 'Avg HT',   dataIndex: 'AvgHTSecs',    key: 'AvgHTSecs',    width: 80,  align: 'center', sorter: (a, b) => (a.AvgHTSecs ?? 0) - (b.AvgHTSecs ?? 0),    render: p('AvgHTSecs') },
      ],
    },
    {
      title: 'QA & Compliance',
      children: [
        { title: 'QA Evals',         dataIndex: 'EvalCount',           key: 'EvalCount',           width: 90,  align: 'right',  sorter: (a, b) => (a.EvalCount ?? 0) - (b.EvalCount ?? 0),               render: lbn },
        { title: 'QA Score',         dataIndex: 'AvgQAScore',          key: 'AvgQAScore',          width: 90,  align: 'center', sorter: (a, b) => (a.AvgQAScore ?? -1) - (b.AvgQAScore ?? -1),           render: p('AvgQAScore') },
        { title: 'Occurrences',      dataIndex: 'OccurrenceCount',     key: 'OccurrenceCount',     width: 95,  align: 'center', sorter: (a, b) => (a.OccurrenceCount ?? 0) - (b.OccurrenceCount ?? 0),   render: p('OccurrenceCount') },
        { title: 'Errors',           dataIndex: 'ErrorCount',          key: 'ErrorCount',          width: 75,  align: 'center', sorter: (a, b) => (a.ErrorCount ?? 0) - (b.ErrorCount ?? 0),             render: p('ErrorCount') },
        { title: 'Errors %', key: 'ErrorsPct', width: 75, align: 'right',
          sorter: (a, b) => {
            const pa = errCalls(a) ? a.ErrorCount / errCalls(a) : -1;
            const pb = errCalls(b) ? b.ErrorCount / errCalls(b) : -1;
            return pa - pb;
          },
          render: (_, r) => {
            if (!errCalls(r) || r.ErrorCount == null) return '—';
            return `${(r.ErrorCount / errCalls(r) * 100).toFixed(1)}%`;
          },
        },
        ...(!selectedMonth || selectedMonth >= '2026-08' ? [
          { title: 'Compliance', dataIndex: 'ComplianceCount', key: 'ComplianceCount', width: 95, align: 'center', sorter: (a, b) => (a.ComplianceCount ?? 0) - (b.ComplianceCount ?? 0), render: p('ComplianceCount') },
          { title: 'Recoach',    dataIndex: 'RecoachingCount', key: 'RecoachingCount', width: 85, align: 'center', sorter: (a, b) => (a.RecoachingCount ?? 0) - (b.RecoachingCount ?? 0), render: p('RecoachingCount') },
        ] : []),
      ],
    },
    {
      title: 'Timecard & Schedule',
      children: [
        { title: 'Worked',                dataIndex: 'WorkedHrs',            key: 'WorkedHrs',            width: 72,  align: 'right',  sorter: (a, b) => (a.WorkedHrs ?? 0) - (b.WorkedHrs ?? 0),                       render: lbh },
        { title: 'PTO',                   dataIndex: 'PTOHrs',               key: 'PTOHrs',               width: 60,  align: 'right',  sorter: (a, b) => (a.PTOHrs ?? 0) - (b.PTOHrs ?? 0),                             render: lbh },
        { title: 'Other',                 dataIndex: 'OtherHrs',             key: 'OtherHrs',             width: 60,  align: 'right',  sorter: (a, b) => (a.OtherHrs ?? 0) - (b.OtherHrs ?? 0),                         render: lbh },
        { title: 'Scheduled',             dataIndex: 'ScheduledHrs',         key: 'ScheduledHrs',         width: 82,  align: 'right',  sorter: (a, b) => (a.ScheduledHrs ?? 0) - (b.ScheduledHrs ?? 0),                 render: lbh },
        ...(canSeeAdherence ? [{ title: 'Adherence',             dataIndex: 'AdherencePct',         key: 'AdherencePct',         width: 90,  align: 'center', sorter: (a, b) => (a.AdherencePct ?? -1) - (b.AdherencePct ?? -1),               render: p('AdherencePct') }] : []),
        ...(!selectedMonth || selectedMonth >= '2026-08' ? [
          { title: 'Absence Notifications', dataIndex: 'ShortTermNoticeCount', key: 'ShortTermNoticeCount', width: 100, align: 'center', sorter: (a, b) => (a.ShortTermNoticeCount == null) - (b.ShortTermNoticeCount == null) || (a.ShortTermNoticeCount ?? 0) - (b.ShortTermNoticeCount ?? 0), render: p('ShortTermNoticeCount') },
        ] : []),
      ],
    },
    ...(lbData.some((r) => r.UpsellCount != null) ? [{
      title: 'Upsells',
      children: [
        { title: 'Count', dataIndex: 'UpsellCount', key: 'UpsellCount', width: 75, align: 'right',
          sorter: (a, b) => (a.UpsellCount ?? -1) - (b.UpsellCount ?? -1),
          render: lbn },
      ],
    }] : []),
    {
      title: 'Cheers for Peers',
      children: [
        { title: 'Received', dataIndex: 'CheersFromPeers', key: 'CheersFromPeers', width: 85, align: 'right',
          sorter: (a, b) => (a.CheersFromPeers ?? -1) - (b.CheersFromPeers ?? -1),
          render: lbn },
      ],
    },
  ];

  // Matched on the top-level header text, so a new metric group is hidden from
  // the limited view by default rather than exposed by omission.
  const LIMITED_VIEW_HEADERS = ['Month', 'Campaign', 'Agent', 'Contact Stats (Inbound)'];
  const visibleLbColumns = isLimitedView
    ? lbColumns.filter((c) => LIMITED_VIEW_HEADERS.includes(c.title))
    : lbColumns;

  const filters = (
    <Form layout="inline" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
      {view === 'stars' ? (
        <Form.Item label="Quarter">
          <Select
            allowClear={false}
            placeholder="Select quarter..."
            options={quarterOptions}
            value={starsQuarter}
            onChange={setStarsQuarter}
            style={{ width: 130 }}
          />
        </Form.Item>
      ) : (
        <Form.Item label="Month">
          <Select
            allowClear={false}
            placeholder="Select month..."
            options={monthOptions}
            value={selectedMonth}
            onChange={setSelectedMonth}
            style={{ width: 130 }}
          />
        </Form.Item>
      )}
      {!embedded && (
      <Form.Item label="Campaign" required>
        <Select
          showSearch filterOption={selectFilter}
          placeholder="Select a campaign..."
          options={campaignOptions}
          value={campaign}
          onChange={setCampaign}
          style={{ width: 220 }}
        />
      </Form.Item>
      )}
      {!embedded && !isAgent && (
        <Form.Item label="Team">
          <Select
            allowClear showSearch filterOption={selectFilter}
            placeholder="All teams"
            options={teamOptions}
            value={teamId}
            onChange={setTeamId}
            style={{ width: 180 }}
          />
        </Form.Item>
      )}
      {/* Job also on Campaign Summary's Agent Goals (embedded). */}
      <Form.Item label="Job">
        <Select
          allowClear showSearch filterOption={selectFilter}
          placeholder="All jobs"
          options={jobOptions}
          value={jobId}
          onChange={setJobId}
          style={{ width: 180 }}
        />
      </Form.Item>
    </Form>
  );

  return (
    <>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Leaderboard</Typography.Title>
          {!isLimitedView && (
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { label: 'Stats', value: 'stats' },
                { label: 'Goals', value: 'goals' },
                // Admin and QA admin, and supervisors (JobID 6) -- routes/leaderboard.
                ...(canSeeStars ? [{ label: 'Superior Stars', value: 'stars' }] : []),
              ]}
            />
          )}
        </div>
      )}

      {filters}

      {!campaign ? (
        <Typography.Text type="secondary" style={{ display: 'block', marginTop: 40, textAlign: 'center' }}>
          {embedded
            ? 'No campaign selected.'
            : 'Select a campaign above to view the leaderboard.'}
        </Typography.Text>
      ) : (view === 'stats' || isLimitedView) ? (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
            Key metric cells show the value with ranking vs. peers in the same campaign and month (e.g. 2/34 = 2nd best out of 34).
            Green = top 30% &nbsp;|&nbsp; Blue = middle 40% &nbsp;|&nbsp; Orange = 30–50th &nbsp;|&nbsp; Red = bottom 30%.
          </Typography.Paragraph>
          <div style={{ fontSize: 12 }}>
            <Table
              rowKey={(r) => `${r.EmployeeID}-${r.MonthKey}-${r.Campaign}`}
              dataSource={lbData}
              columns={visibleLbColumns.map(toResizable)}
              loading={lbLoading}
              size="small"
              scroll={{ x: 'max-content' }}
              sticky={{ offsetHeader: 64 }}
              pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} rows` }}
              locale={{ emptyText: 'No data for this period.' }}
              components={{ header: { cell: ResizableTitle } }}
            />
          </div>
        </>
      ) : view === 'stars' ? (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
            Superior Stars grades the whole quarter as one period, not three months averaged
            together: QA Score is weighted by evaluations and Errors % is total errors over
            total inbound calls, so a heavy month counts for what it holds. Both pool every
            campaign the agent worked; the campaign above picks who appears and which goal
            applies. The Stars Adherence Level is uploaded per quarter as the level itself
            (0 Platinum, 1 Gold, 2 Silver, 3 not achieved).
            An agent is <strong>Eligible</strong> when they were scored in all
            {' '}{starsMonths.length || 3} months, were employed at quarter end and have a
            Stars Adherence Level for the quarter.
          </Typography.Paragraph>
          <div style={{ marginBottom: 12 }}>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={exportStars}
              disabled={!starsData.length}
            >
              Export Excel
            </Button>
          </div>
          <div style={{ fontSize: 12 }}>
            <Table
              rowKey="EmployeeID"
              dataSource={starsData}
              columns={starColumns}
              loading={starsLoading}
              size="small"
              scroll={{ x: 'max-content' }}
              sticky={{ offsetHeader: 64 }}
              pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} agents` }}
              locale={{ emptyText: 'No data for this quarter.' }}
            />
          </div>
        </>
      ) : (
        <>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 12, fontSize: 12 }}>
            Goal levels: <strong style={{ color: TIER.platinum }}>Platinum</strong> &nbsp;|&nbsp;
            <strong style={{ color: TIER.gold }}>Gold</strong> (Target) &nbsp;|&nbsp;
            <strong style={{ color: TIER.silver }}>Silver</strong> &nbsp;|&nbsp;
            <strong style={{ color: TIER.below }}>Below Goal</strong>.
            A dash (—) means no goal is configured for that metric in this campaign.
            Adherence % uses the overall (non-campaign) goal.
          </Typography.Paragraph>
          {goalSummary.length > 0 && (
            <Table
              rowKey="key"
              dataSource={goalSummary}
              columns={summaryColumns}
              size="small"
              pagination={false}
              style={{ marginBottom: 20, maxWidth: 520 }}
            />
          )}
          <div style={{ fontSize: 12 }}>
            <Table
              rowKey={(r) => `${r.EmployeeID}-${r.MonthKey}-${r.Campaign}`}
              dataSource={lbData}
              columns={goalColumns}
              loading={lbLoading}
              size="small"
              scroll={{ x: 'max-content' }}
              sticky={{ offsetHeader: 64 }}
              pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} rows` }}
              locale={{ emptyText: 'No data for this period.' }}
            />
          </div>
        </>
      )}
    </>
  );
}
